import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin } from 'rxjs';
import { UserForm } from 'src/app/classes/form-groups/user-form';
import { Company, User, UserAccountRole } from 'src/app/classes/models';
import { ToastService } from 'src/app/services/toast.service';
import { UserService } from 'src/app/services/user.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-edit-user-modal',
  templateUrl: './edit-user-modal.component.html',
  styleUrl: './edit-user-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class EditUserModalComponent implements OnInit {
  constructor(
    private activeModal: NgbActiveModal,
    private UtilityService: UtilityService,
    private ToastService: ToastService,
    private UserService: UserService
  ) {
    const ContactForm = this.userForm.controls.Contact;

    if (ContactForm) {
      const MailingAddressControl = ContactForm.controls.MailingAddress;

      if (MailingAddressControl) {
        // Currently unused during client user upload
        ContactForm.removeControl('MailingAddress');
        ContactForm.updateValueAndValidity();
      }
    }
  }

  @Input() User?: User;
  @Input() Company?: Company;

  userForm: UserForm = new UserForm();
  userAccountRoles?: UserAccountRole[];
  phoneMask = this.UtilityService.phoneMask;

  close() {
    this.activeModal.dismiss('Cross Click');
  }

  save() {
    const errors = this.UtilityService.generateValidationErrorsObject(
      this.userForm
    );

    if (errors) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot save edited user details. Form is missing data.',
        { returnError: true }
      );
    }

    if (!this.User) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing user information.',
        { returnError: true }
      );
    }

    const { CompanyId, Contact } = this.User;

    if (!Contact) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing user contact information.',
        { returnError: true }
      );
    }

    const { email, phone1 } = Contact;

    forkJoin([
      this.UserService.checkForDuplicateUsers(
        this.UtilityService.stringifyObjectsInQueryObject({
          CompanyId,
          email
        })
      )
    ]).subscribe({
      next: ([duplicateUsers]) => {
        if (duplicateUsers.length > 0) {
          throw this.ToastService.addErrorToast(
            'Error',
            'User with email already exists for this client.',
            { returnError: true }
          );
        }

        this.activeModal.close({
          ...this.userForm.value,
          email,
          phone: phone1,
          matchingExistingUser: duplicateUsers.length > 0,
          missingRequiredData: false
        });
      }
    });
  }

  ngOnInit(): void {
    if (!this.User) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot edit user details. No user found.',
        { returnError: true }
      );
    }

    if (!this.Company) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Company Information.',
        {
          returnError: true
        }
      );
    }

    this.userForm.patchValue(this.User);

    this.UserService.getUserAccountRoles().subscribe({
      next: (UserAccountRoles) => {
        this.userAccountRoles = UserAccountRoles;
      }
    });

    this.userForm.valueChanges.subscribe({
      next: (changes) => {
        if (this.User) {
          Object.assign(this.User, changes);
        }
      }
    });
  }
}
